'use client'

import { useEffect, useMemo, useState } from 'react'
import axios from '@/utils/axiosInterceptor'

import styles from './Calendar.module.scss'
import classNames from 'classnames/bind'
import Badge from '../Badge/Badge'
import Icon from '../Icon/Icon'
const classes = classNames.bind(styles)

export default function Calendar({}) {
    const currentDate = useMemo(() => new Date(), [])
    const currentYear = currentDate.getFullYear()

    const [eventsByYear, setEventsByYear] = useState(() => ({
        [currentYear]: createEmptyCalendar(),
        [currentYear + 1]: createEmptyCalendar(),
    }))
    const [availableYears, setAvailableYears] = useState([currentYear, currentYear + 1])
    const [selectedYear, setSelectedYear] = useState(currentYear)

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const {
                    data: { data: fetchedEvents },
                } = await axios.get(`/assessment/api/dashboard/events`)

                const { eventsByYear: normalisedEvents, years } = transformEvents(
                    fetchedEvents,
                    currentYear,
                    currentDate
                )

                setEventsByYear(normalisedEvents)
                setAvailableYears(years)
                setSelectedYear((prev) => (years.includes(prev) ? prev : years[0] ?? currentYear))
            } catch (error) {
                console.error('Error loading dashboard events', error)
            }
        }

        fetchEvents()
    }, [currentYear])

    const yearIndex = availableYears.indexOf(selectedYear)
    const canGoBack = yearIndex > 0
    const canGoForward = yearIndex !== -1 && yearIndex < availableYears.length - 1

    const monthEventsForYear = eventsByYear[selectedYear] || createEmptyCalendar()
    const upcomingEvents = useMemo(() => getUpcomingEvents(eventsByYear, currentDate), [eventsByYear, currentDate])

    return (
        <div className={classes('container')}>
            <div className={classes('header')}>
                <h5>Personal Tax dates</h5>
                <div className={classes('year-selector')}>
                    <button
                        type="button"
                        className={classes('year-button', { hidden: !canGoBack })}
                        onClick={() => canGoBack && setSelectedYear(availableYears[yearIndex - 1])}
                        disabled={!canGoBack}
                        tabIndex={canGoBack ? 0 : -1}
                        aria-hidden={!canGoBack}
                        aria-label="View previous year"
                    >
                        <Icon name="chevron-left" className={classes('year-icon')} />
                    </button>
                    <Badge text={selectedYear} color="gray" />
                    <button
                        type="button"
                        className={classes('year-button', { hidden: !canGoForward })}
                        onClick={() => canGoForward && setSelectedYear(availableYears[yearIndex + 1])}
                        disabled={!canGoForward}
                        tabIndex={canGoForward ? 0 : -1}
                        aria-hidden={!canGoForward}
                        aria-label="View next year"
                    >
                        <Icon name="chevron-right" className={classes('year-icon')} />
                    </button>
                </div>
            </div>

            <div className={classes('calendar')}>
                {Object.keys(monthEventsForYear).map((month, i) => {
                    const hasEvents = monthEventsForYear[month].length > 0
                    const { isCurrent, isPast } = getMonthState({
                        currentDate,
                        monthIndex: i,
                        selectedYear,
                    })

                    return (
                        <div
                            key={month}
                            className={classes('month-cell', { current: isCurrent, past: isPast })}
                        >
                            <span className={classes('month')}>
                                {month.slice(0, 3)}
                                {hasEvents && <div className={classes('dot')}></div>}
                            </span>
                        </div>
                    )
                })}
            </div>

            <div className={classes('event-list')}>
                {upcomingEvents.map((event) => (
                    <EventDetails key={event.id} event={event} />
                ))}
            </div>
        </div>
    )
}

const EventDetails = ({ event }) => {
    const { name, description, date, month, year, isCurrentMonth } = event

    return (
        <div className={classes('event-details')}>
            <div className={classes('event-header')}>
                <span className={classes('title')}>{name}</span>
                <span className={classes('date', { upcoming: isCurrentMonth })}>{`${date} ${month} ${year}`}</span>
            </div>
            <p className={classes('description')}>{description}</p>
        </div>
    )
}

const createEmptyCalendar = () =>
    ({
        January: [],
        February: [],
        March: [],
        April: [],
        May: [],
        June: [],
        July: [],
        August: [],
        September: [],
        October: [],
        November: [],
        December: [],
    })

const transformEvents = (eventData = {}, baseYear, currentDate) => {
    const eventsByYear = {}
    const now = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())

    Object.entries(eventData).forEach(([monthName, events], monthIndex) => {
        events.forEach((event) => {
            let targetYears = [baseYear, baseYear + 1]

            if (event.year) {
                targetYears = [event.year]
            } else {
                const eventDateForBaseYear = new Date(baseYear, monthIndex, event.date)
                const includeNextYear = eventDateForBaseYear < now

                targetYears = includeNextYear ? [baseYear, baseYear + 1] : [baseYear]
            }

            targetYears.forEach((year) => {
                if (!eventsByYear[year]) {
                    eventsByYear[year] = createEmptyCalendar()
                }

                eventsByYear[year][monthName] = [
                    ...eventsByYear[year][monthName],
                    { ...event, year, monthIndex },
                ].sort((a, b) => a.date - b.date)
            })
        })
    })

    const years = Object.keys(eventsByYear)
        .map((year) => Number(year))
        .sort((a, b) => a - b)

    if (years.length === 0) {
        const currentYear = baseYear
        eventsByYear[currentYear] = createEmptyCalendar()
        eventsByYear[currentYear + 1] = createEmptyCalendar()
        return { eventsByYear, years: [currentYear, currentYear + 1] }
    }

    return { eventsByYear, years }
}

const getUpcomingEvents = (eventsByYear, currentDate) => {
    const now = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())
    const upcoming = []

    Object.entries(eventsByYear).forEach(([yearKey, months]) => {
        const year = Number(yearKey)

        Object.entries(months).forEach(([monthName, events]) => {
            events.forEach((event) => {
                const eventDate = new Date(year, event.monthIndex, event.date)
                if (eventDate < now) return

                upcoming.push({
                    ...event,
                    month: monthName,
                    year,
                    isCurrentMonth: year === now.getFullYear() && event.monthIndex === now.getMonth(),
                    id: `${event.name || 'event'}-${year}-${event.monthIndex}-${event.date}`,
                    eventDate,
                })
            })
        })
    })

    return upcoming
        .sort((a, b) => a.eventDate.getTime() - b.eventDate.getTime())
        .map(({ eventDate, ...rest }) => rest)
}

const getMonthState = ({ currentDate, monthIndex, selectedYear }) => {
    const nowYear = currentDate.getFullYear()
    const nowMonth = currentDate.getMonth()

    if (selectedYear < nowYear) {
        return { isCurrent: false, isPast: true }
    }

    if (selectedYear > nowYear) {
        return { isCurrent: false, isPast: false }
    }

    return {
        isCurrent: monthIndex === nowMonth,
        isPast: monthIndex < nowMonth,
    }
}
